package com.evoapps.evoshift.domain.usecase.staff

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.InvalidStaffRecordException
import com.evoapps.evoshift.domain.model.StaffChecks
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.model.StaffRecord
import com.evoapps.evoshift.domain.model.problems
import com.evoapps.evoshift.domain.repository.StaffRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlin.time.Instant

/*
 * SH-47: the use cases behind the staff fields. Who may call what is the screens' (Gate) and the
 * rules' (firestore.rules): the dates are the person's and the owner's / financial managers',
 * statuses and notes also the operational managers'.
 */

internal val systemToday: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) }

/** What one person's staff fields show: the parts the reader asked for and may read. */
data class StaffDetails(
    /** The dates (null: not asked for, or nothing recorded). */
    val record: StaffRecord? = null,
    /** The statuses: from [record] when it was read, else from the status document. */
    val readiness: StaffReadiness? = null,
    /** Availability notes (null: not asked for, or none). */
    val notes: String? = null
)

/**
 * [userId]'s staff fields at [workplaceId]. [includeDates]: the dates (owners, financial
 * managers, the person); otherwise only the statuses. [includeNotes]: the availability notes
 * (managers, the person). A part that cannot be read is left out, not an error.
 */
class GetStaffDetailsUseCase(
    private val staffRepository: StaffRepository,
    private val today: () -> LocalDate = systemToday
) {
    suspend operator fun invoke(userId: String, workplaceId: String, includeDates: Boolean, includeNotes: Boolean): Result<StaffDetails> = runCatching {
        val record = if (includeDates) staffRepository.getStaffRecord(userId, workplaceId).getOrThrow() else null
        val readiness = if (includeDates) {
            StaffChecks.readiness(record, userId, workplaceId, today())
        } else {
            staffRepository.getReadinessOf(userId, workplaceId).getOrNull()
        }
        val notes = if (includeNotes) staffRepository.getAvailabilityNotes(userId, workplaceId).getOrNull() else null
        StaffDetails(record = record, readiness = readiness, notes = notes)
    }
}

/**
 * Saves [record]'s dates, with the statuses the managers see worked out for today. Refuses a
 * training valid until before the training ([InvalidStaffRecordException]).
 */
class SaveStaffRecordUseCase(
    private val staffRepository: StaffRepository,
    private val today: () -> LocalDate = systemToday,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(record: StaffRecord, editorId: String): Result<StaffRecord> = runCatching {
        val problems = record.problems()
        if (problems.isNotEmpty()) throw InvalidStaffRecordException(problems)
        val stamped = record.copy(updatedAt = now().toString(), updatedBy = editorId)
        val readiness = StaffChecks.statusOnly(StaffChecks.readiness(stamped, record.userId, record.workplaceId, today()))
        staffRepository.saveStaffRecord(stamped, readiness).getOrThrow()
        stamped
    }
}

/** Saves the availability notes, trimmed and cut to [StaffChecks.NOTES_MAX_LENGTH]; blank removes them. */
class SaveAvailabilityNotesUseCase(private val staffRepository: StaffRepository) {
    suspend operator fun invoke(userId: String, workplaceId: String, notes: String, editorId: String): Result<String> = runCatching {
        val clean = notes.trim().take(StaffChecks.NOTES_MAX_LENGTH)
        staffRepository.saveAvailabilityNotes(userId, workplaceId, clean, editorId).getOrThrow()
        clean
    }
}

/** How many of a team need a manager's attention: checks expired, or ending within 30 days. */
data class StaffAlerts(
    val expired: Int = 0,
    val expiring: Int = 0
) {
    val total: Int get() = expired + expiring
}

/**
 * The statuses of everyone at [workplaceId] (uid → readiness), from the status documents the
 * workplace's managers read. [activeUserIds]: only these count (people who left keep no alerts).
 */
class GetTeamReadinessUseCase(
    private val staffRepository: StaffRepository,
    /** For [alerts]: who still works here (people who left keep no alerts). */
    private val userRepository: UserRepository? = null
) {
    suspend operator fun invoke(workplaceId: String, activeUserIds: Set<String>? = null): Result<Map<String, StaffReadiness>> = runCatching {
        staffRepository.getReadiness(workplaceId).getOrThrow()
            .filter { activeUserIds == null || it.userId in activeUserIds }
            .associateBy { it.userId }
    }

    /** The dashboards' counts for [workplaceId]'s current team. */
    suspend fun alerts(workplaceId: String): Result<StaffAlerts> = runCatching {
        val active = userRepository?.getAssignableEmployees(workplaceId)?.getOrNull()?.mapTo(mutableSetOf()) { it.uid }
        alerts(invoke(workplaceId, active).getOrThrow().values)
    }

    companion object {
        /** The dashboard's counts: a person with an expired check counts as expired, else as expiring. */
        fun alerts(readiness: Collection<StaffReadiness>): StaffAlerts = StaffAlerts(
            expired = readiness.count { it.anyExpired },
            expiring = readiness.count { it.needsAttention && !it.anyExpired }
        )
    }
}
